import { CompleteProfileBody, UserType } from "@ac-expo/api-types";
import { Role } from "@prisma/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import userQueryKeys from "../query-keys/user.query-keys";
import {
  DeleteAccountForm,
  ModifyProfileForm,
  ModifyProProfileForm,
  NewProfessionalForm,
  ProfilePictureForm,
  RoleSelectionForm,
  UpdatePasswordForm,
} from "../schemas";

const modifyProfile = async (data: ModifyProfileForm) => {
  try {
    const res = await privateAxios.patch("/users/me", data);
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useModifyProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: modifyProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.me() });
    },
  });
};

const modifyPassword = async (data: UpdatePasswordForm) => {
  const res = await privateAxios.post("/users/modify-password", data);
  return res.data;
};

export const useModifyPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: modifyPassword,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.me() });
    },
  });
};

const updatePreferences = async (data: {
  pushNotifications: boolean;
  emailNotifications: boolean;
}) => {
  const res = await privateAxios.patch("/users/preferences/me", data);
  return res.data;
};

export const useUpdatePreferences = () => {
  return useMutation({
    mutationFn: updatePreferences,
  });
};

const toggleUserRole = async (role: Role, newStatus: boolean) => {
  const { data } = await privateAxios.patch(`/users/roles`, {
    role,
    newStatus,
  });
  return data;
};

export const useToggleUserRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ role, newStatus }: { role: Role; newStatus: boolean }) =>
      toggleUserRole(role, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.me(),
      });
    },
  });
};

const selectRoles = async (data: RoleSelectionForm) => {
  const { data: response } = await privateAxios.post("/users/roles", data);
  return response;
};

export const useSelectRolesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: selectRoles,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.me(),
      });
    },
  });
};

const newProfessional = async (data: NewProfessionalForm) => {
  const { data: response } = await privateAxios.patch(
    "/users/become-professional",
    data
  );
  return response;
};

export const useNewProfessionalMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: newProfessional,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.me(),
      });
    },
  });
};

const updateUserProfilePicture = async (values: ProfilePictureForm) => {
  const { data } = await privateAxios.patch("/users/picture", values);
  return data;
};

export const useUpdateProfilePictureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserProfilePicture,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.me(),
      });
    },
  });
};

const deleteMyUser = async (params: { withData: boolean }) => {
  try {
    const { data } = await privateAxios.delete("/users/me", {
      params,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useDeleteMyUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteMyUser,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.me(),
      });
    },
  });
};

const deleteUser = async (body: DeleteAccountForm, withData: boolean) => {
  try {
    const { data } = await privateAxios.delete("/auth", {
      params: {
        withData,
      },
      data: body,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
      throw error;
    }

    throw error;
  }
};

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      withData,
    }: {
      data: DeleteAccountForm;
      withData: boolean;
    }) => deleteUser(data, withData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.me(),
      });
    },
  });
};

const modifyProProfile = async (data: ModifyProProfileForm) => {
  try {
    const res = await privateAxios.patch("/users/pro/me", data);
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useModifyProProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: modifyProProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.me() });
    },
  });
};

const acceptUpdatedTerms = async () => {
  try {
    const res = await privateAxios.post("/users/accept-updated-terms");
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useAcceptUpdatedTerms = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acceptUpdatedTerms,
    onSuccess: () => {
      queryClient.setQueryData(userQueryKeys.me(), (data: UserType) => ({
        ...data,
        acceptedTerms: true,
      }));
    },
  });
};

const setActive = async (data: { userId: string; isActive: boolean }) => {
  try {
    const res = await privateAxios.post(`/users/set-active/${data.userId}`, {
      isActive: data.isActive,
    });
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }

    throw error;
  }
};

export const useSetActiveMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setActive,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.all,
      });
    },
  });
};

const completeProfile = async (data: CompleteProfileBody) => {
  try {
    const res = await privateAxios.patch("/users/complete-profile", data);
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useCompleteProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: completeProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.me() });
    },
  });
};
